import { config } from '../config';
import { db } from '../data/harnessDb';
import { StorageError } from '../app/errors';
import { log } from '../app/logger';
import type { RunRecord } from '../types';

/**
 * Persists completed runs in IndexedDB.
 *
 * Pattern: `db-driven-state-over-in-memory`. A run taken at a venue must survive the PWA being
 * backgrounded, the tab being evicted under memory pressure, and the phone being locked and
 * unlocked — all of which happen routinely between the first half and the moment Andrew has a
 * hand free to read the result. Holding results in React state would lose the measurement to the
 * exact conditions the measurement is about.
 *
 * The store is capped, oldest-first, so a season of venue runs cannot grow without bound.
 */

const MODULE = 'results';

export async function saveRun(run: RunRecord): Promise<void> {
  const handle = db();
  try {
    await handle.runs.put({
      id: run.id,
      startedAt: run.startedAt,
      condition: run.condition,
      payload: run,
    });
    await trimHistory();
    log.info(MODULE, 'run persisted', { id: run.id, condition: run.condition, n: run.samples.length });
  } catch (cause) {
    // Surfaced, not swallowed. Losing the record of a measurement taken once, at a venue, that
    // cannot be reproduced at a desk, is the worst failure this harness has.
    throw new StorageError(
      'Could not persist the run. Export it now before navigating away — the measurement is only in memory.',
      { cause, context: { id: run.id } },
    );
  }
}

export async function loadRuns(): Promise<RunRecord[]> {
  const handle = db();
  try {
    const rows = await handle.runs.orderBy('startedAt').reverse().toArray();
    // Runs stored before the build stamp existed have no `environment.buildId`. They are NOT
    // backfilled — inventing a version for a result whose bundle is unknown would be worse than
    // saying so. They are normalised to `'unknown'` here, at the one boundary where legacy rows
    // enter, so every consumer downstream can rely on the field being a string.
    return rows.map((r) => {
      const run = r.payload as RunRecord;
      return {
        ...run,
        // `?? null` and NOT `?? 0`. A run stored before contamination detection existed cannot
        // attest that it was clean; recording it as 0 would be asserting a check that never ran.
        foreignHeatWorkersDuringRun: run.foreignHeatWorkersDuringRun ?? null,
        environment: run.environment?.buildId
          ? run.environment
          : { ...run.environment, buildId: 'unknown' },
      };
    });
  } catch (cause) {
    throw new StorageError('Could not read stored runs.', { cause });
  }
}

export async function deleteRun(id: string): Promise<void> {
  const handle = db();
  try {
    await handle.runs.delete(id);
  } catch (cause) {
    throw new StorageError('Could not delete the run.', { cause, context: { id } });
  }
}

async function trimHistory(): Promise<void> {
  const handle = db();
  const total = await handle.runs.count();
  const excess = total - config.HARNESS_RUN_HISTORY_CAP;
  if (excess <= 0) return;
  const oldest = await handle.runs.orderBy('startedAt').limit(excess).primaryKeys();
  await handle.runs.bulkDelete(oldest);
  log.info(MODULE, 'trimmed run history', { removed: oldest.length });
}
